/**
 * @file Represents a grid component used to organize filter controls.
 * The FiltersGrid component provides a structured layout for dropdown filter controls.
 */
import React, { useContext } from 'react';
import Dropdown from './Dropdown';
import {
    FILTER_IDS,
    FilterStatesContext,
    PEER_FILTER_ACTIONS
} from '../contexts/filterStates';
import Box from '@mui/material/Box';

/**
 * The `FiltersGrid` component renders a responsive grid layout
 * for an array of filter controls. The labels for these filters
 * are defined in an array below. The states and options for these
 * filters are maintained in the filterState useContext object.
 *
 * @component
 * @returns {React.ReactElement} The rendered FiltersGrid component.
 */
const FiltersGrid = () => {
    const filtersConfig = [
        {
            id: FILTER_IDS.FUND_TYPE,
            label: 'Fund Type',
            dataAnalyticsId: 'fund-type'
        },
        {
            id: FILTER_IDS.GEOGRAPHY,
            label: 'Geography',
            dataAnalyticsId: 'geography'
        },
        {
            id: FILTER_IDS.STYLE,
            label: 'Style',
            dataAnalyticsId: 'style'
        },
        {
            id: FILTER_IDS.MARKET_CAP,
            label: 'Market Cap',
            dataAnalyticsId: 'market-cap'
        }
    ];

    const { peerFilterStates, peerFilterDispatch } =
        useContext(FilterStatesContext);

    return (
        <Box display='flex' flexDirection={{ xs: 'column', md: 'row' }}>
            {filtersConfig.map((filter, index) => (
                <Box
                    id={filter.id + 'DropDownBox'}
                    key={filter.label}
                    flex={1}
                    display='flex'
                    position='relative'
                    flexDirection='column'
                    justifyContent='center'
                    paddingBottom={{ xs: 1.5, md: 0 }}
                    //spacing between each item, not the last item
                    paddingRight={{
                        md: index < filtersConfig.length - 1 ? 0.5 : 0
                    }}
                >
                    <Dropdown
                        id={filter.id + 'Dropdown'}
                        dataAnalyticsId={`${filter.dataAnalyticsId}-dropdown`}
                        label={filter.label}
                        onChange={(e, v) =>
                            peerFilterDispatch({
                                type: PEER_FILTER_ACTIONS.SET_FILTERS,
                                payload: [{ id: filter.id, value: v }]
                            })
                        }
                        values={peerFilterStates.filters[filter.id].value}
                        options={peerFilterStates.filters[filter.id].options}
                    />
                </Box>
            ))}
        </Box>
    );
};

export default FiltersGrid;
